/**
 * Automations Page — list and manage CRM workflow automations.
 * Includes template gallery for quick-start and toggle active/inactive.
 */

import { useEffect, useState, useCallback } from 'react'
import { Zap, Plus, Play, Pause, Trash2, Clock, AlertTriangle, LayoutTemplate } from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import { AutomationBuilder } from '@/components/crm/AutomationBuilder'
import type { Automation, AutomationTemplate } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  EmptyState,
  useToast,
} from '@/components/ui'

export function AutomationsPage() {
  const { fetchAutomations, toggleAutomation, deleteAutomation, fetchAutomationTemplates, createAutomation } = useCRM()
  const { addToast } = useToast()
  const [automations, setAutomations] = useState<Automation[]>([])
  const [templates, setTemplates] = useState<AutomationTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showBuilder, setShowBuilder] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    const [autos, tmpls] = await Promise.all([fetchAutomations(), fetchAutomationTemplates()])
    setAutomations(autos)
    setTemplates(tmpls)
    setIsLoading(false)
  }, [fetchAutomations, fetchAutomationTemplates])

  useEffect(() => { load() }, [load])

  const handleToggle = useCallback(async (id: string) => {
    await toggleAutomation(id)
    load()
  }, [toggleAutomation, load])

  const handleConfirmedDelete = useCallback(async () => {
    if (!confirmDelete) return
    setIsDeleting(true)
    const ok = await deleteAutomation(confirmDelete)
    setIsDeleting(false)
    if (ok) {
      addToast({ type: 'success', title: 'Automation deleted' })
      load()
    } else {
      addToast({ type: 'error', title: 'Delete failed' })
    }
    setConfirmDelete(null)
  }, [confirmDelete, deleteAutomation, load, addToast])

  const handleCreateFromTemplate = useCallback(async (template: AutomationTemplate) => {
    const result = await createAutomation({
      name: template.name,
      description: template.description,
      trigger: template.trigger,
      actions: template.actions,
      is_active: false,
    })
    if (result) {
      addToast({ type: 'success', title: 'Automation created', description: `"${template.name}" is ready to activate.` })
      setShowTemplates(false)
      load()
    } else {
      addToast({ type: 'error', title: 'Failed to create automation', description: 'Check your connection and try again.' })
    }
  }, [createAutomation, load, addToast])

  const TRIGGER_LABELS: Record<string, string> = {
    contact_created: 'Contact created',
    contact_tag_added: 'Tag added',
    deal_created: 'Deal created',
    deal_stage_changed: 'Deal stage changed',
    deal_won: 'Deal won',
    deal_lost: 'Deal lost',
    order_status_changed: 'Order status changed',
    contact_idle: 'Contact idle',
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border">
        <div className="py-3 flex items-center gap-3">
          <Zap className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Automations</span>
          <span className="text-xs text-muted-foreground">({automations.length})</span>
          <div className="flex-1" />
          <button onClick={() => setShowTemplates(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-lg hover:bg-muted-foreground/10">
            <LayoutTemplate className="w-3.5 h-3.5" />Templates
          </button>
          <button onClick={() => setShowBuilder(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90">
            <Plus className="w-4 h-4" />New Automation
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Spinner />
          </div>
        ) : automations.length === 0 ? (
          <EmptyState icon={Zap} title="No automations yet" description="Create one or start from a template." />
        ) : (
          <div className="space-y-3">
            {automations.map((auto) => (
              <div key={auto.id} className="bg-card border border-border rounded-lg p-4 hover:border-muted-foreground/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium text-foreground">{auto.name}</h3>
                      <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                        auto.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                      }`}>
                        {auto.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {auto.description && <p className="text-xs text-muted-foreground mb-2">{auto.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Trigger: {TRIGGER_LABELS[auto.trigger?.type] || auto.trigger?.type}</span>
                      <span>{auto.actions.length} action{auto.actions.length !== 1 ? 's' : ''}</span>
                      {auto.runCount > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />Ran {auto.runCount}x
                        </span>
                      )}
                      {auto.lastError && (
                        <span className="flex items-center gap-0.5 text-destructive">
                          <AlertTriangle className="w-3 h-3" />Error
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button onClick={() => handleToggle(auto.id)}
                      className="p-1.5 rounded-sm hover:bg-muted" title={auto.isActive ? 'Deactivate' : 'Activate'}>
                      {auto.isActive ? <Pause className="w-4 h-4 text-muted-foreground" /> : <Play className="w-4 h-4 text-success" />}
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Delete automation"
                      className="hover:bg-destructive/10"
                      onClick={() => setConfirmDelete(auto.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Templates modal */}
      {showTemplates && (
        <Modal open onOpenChange={(o) => { if (!o) setShowTemplates(false) }}>
          <ModalContent
            size="md"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <ModalHeader><ModalTitle>Automation Templates</ModalTitle></ModalHeader>
            <ModalBody className="space-y-3">
              {templates.map((t) => (
                <div key={t.id} className="border border-border rounded-lg p-3 hover:border-primary/40 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-foreground">{t.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                      <span className="text-[10px] text-muted-foreground mt-1 inline-block">{t.category}</span>
                    </div>
                    <Button size="sm" onClick={() => handleCreateFromTemplate(t)}>Use</Button>
                  </div>
                </div>
              ))}
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setShowTemplates(false)}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {showBuilder && <AutomationBuilder onClose={() => { setShowBuilder(false); load() }} />}

      {confirmDelete && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Delete this automation?</ModalTitle></ModalHeader>
            <ModalBody><p className="text-sm text-muted-foreground">This cannot be undone.</p></ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="destructive" isLoading={isDeleting} onClick={handleConfirmedDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  )
}
